import { BrutalDialog } from "../brutal-dialog";
import { ActionForm } from "../action-form";
import { SubmitButton } from "../submit-button";
import { Cross2Icon } from "@radix-ui/react-icons";
import { saveNoteAction, deleteNoteAction } from "../../app/dashboard/actions";
import { DashboardData } from "../../server/services/dashboard-service";
import { todayKey } from "./utils";

export function JournalPanel({ 
  rangeView, 
  visitorMode = false 
}: { 
  rangeView: DashboardData["rangeView"]; 
  visitorMode?: boolean; 
}) {
  const today = todayKey(rangeView.timezone);

  return (
    <details 
      suppressHydrationWarning
      className="panel-brutal group bg-surface min-h-0 transition-all [&[open]]:flex-1 [&:not([open])]:shrink-0 [&[open]]:grid [&[open]]:grid-rows-[max-content_minmax(0,1fr)]" 
      open
    >
      <summary className="flex justify-between items-start mb-0 group-open:mb-6 group-open:border-b-2 group-open:border-ink group-open:pb-4 shrink-0 cursor-pointer list-none select-none [&::-webkit-details-marker]:hidden">
        <div>
          <p className="font-mono text-xs font-bold tracking-widest uppercase mb-1">Journal</p>
          <h2 className="font-serif text-3xl font-bold">每日笔记 <span className="text-xl text-ink-light font-mono ml-2">({rangeView.label})</span></h2>
        </div>
        <span className="text-ink text-4xl leading-none font-normal shrink-0 group-open:rotate-45 transition-transform duration-200">+</span>
      </summary>
      
      <div className="h-full flex flex-col min-h-0 overflow-hidden">
        {visitorMode ? null : (
        <div className="mb-8 shrink-0">
          <BrutalDialog
            title="New Journal Entry"
            trigger={
              <button type="button" className="btn-brutal inline-flex items-center gap-2 cursor-pointer w-fit text-sm">
                <span className="text-lg leading-none font-bold">+</span> NEW JOURNAL ENTRY
              </button>
            }
          >
            {(close) => (
              <ActionForm className="flex flex-col gap-4" action={saveNoteAction} resetOnSuccess onSuccess={close}>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex flex-col gap-1">
                    <span className="font-mono text-xs font-bold uppercase">Date</span>
                    <input className="input-brutal w-full font-mono text-sm" name="date" type="date" defaultValue={today} />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="font-mono text-xs font-bold uppercase">Visibility</span>
                    <select className="input-brutal w-full font-mono text-sm" name="visibility" defaultValue="private">
                      <option value="private">私密 (PRIVATE)</option>
                      <option value="public">公开 (PUBLIC)</option>
                    </select>
                  </label>
                </div>
                <label className="flex flex-col gap-1 mt-2">
                  <span className="font-mono text-xs font-bold uppercase">Content</span>
                  <textarea 
                    className="input-brutal w-full min-h-[160px] font-serif text-lg leading-relaxed resize-y" 
                    name="body" 
                    placeholder="写下今天对计划、偏移与事实的解释..." 
                  />
                </label>
                <div className="flex justify-end mt-4">
                  <SubmitButton className="btn-brutal" pendingText="SAVING...">保存笔记</SubmitButton>
                </div>
              </ActionForm>
            )}
          </BrutalDialog>
        </div>
      )}
      
      <div className="flex flex-col gap-6 flex-1 overflow-y-auto brutal-scrollbar pr-2 mr-[-8px]">
        {rangeView.notes.length === 0 && (
           <div className="flex-1 flex items-center justify-center border-2 border-dashed border-ink/20 shrink-0 min-h-[120px]">
             <p className="font-mono text-ink-light text-center italic">
               这段时间内没有笔记。
             </p>
           </div>
        )}
        {rangeView.notes.map((note) => (
          <article className="border-l-4 border-ink pl-4 py-1" key={note.id}>
            {visitorMode ? (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <time className="font-mono text-sm font-bold bg-ledger text-ledger-foreground px-2 py-0.5">{note.date}</time>
                </div>
                <p className="font-serif text-lg leading-relaxed whitespace-pre-wrap">{note.body}</p>
              </>
            ) : (
              <div className="flex flex-col gap-3">
                <BrutalDialog
                  title="Edit Journal Entry"
                  trigger={
                    <div className="flex justify-between items-center group/note cursor-pointer">
                      <div className="flex items-center gap-3">
                        <time className="font-mono text-sm font-bold bg-ledger text-ledger-foreground px-2 py-0.5">{note.date}</time>
                        <span className="font-mono text-xs px-2 py-0.5 bg-paper border border-ink">
                          {note.visibility === "public" ? "PUBLIC" : "PRIVATE"}
                        </span>
                      </div>
                      <span className="font-mono text-xs opacity-0 group-hover/note:opacity-100 transition-opacity">EDIT</span>
                    </div>
                  }
                >
                  {(close) => (
                    <div className="flex flex-col gap-4">
                      <ActionForm className="flex flex-col gap-4" action={saveNoteAction} onSuccess={close}>
                        <input type="hidden" name="id" value={note.id} />
                        <div className="grid grid-cols-2 gap-4">
                          <label className="flex flex-col gap-1">
                            <span className="font-mono text-xs font-bold uppercase">Date</span>
                            <input className="input-brutal w-full font-mono text-sm" name="date" type="date" defaultValue={note.date} />
                          </label>
                          <label className="flex flex-col gap-1">
                            <span className="font-mono text-xs font-bold uppercase">Visibility</span>
                            <select className="input-brutal w-full font-mono text-sm" name="visibility" defaultValue={note.visibility}>
                              <option value="private">私密 (PRIVATE)</option>
                              <option value="public">公开 (PUBLIC)</option>
                            </select>
                          </label>
                        </div>
                        <label className="flex flex-col gap-1 mt-2">
                          <span className="font-mono text-xs font-bold uppercase">Content</span>
                          <textarea 
                            className="input-brutal w-full min-h-[160px] font-serif text-lg leading-relaxed resize-y" 
                            name="body" 
                            defaultValue={note.body}
                          />
                        </label>
                        <div className="flex justify-between items-center mt-4 pt-4 border-t-2 border-dashed border-ink/30">
                          <button
                            className="inline-flex items-center gap-2 border-2 border-danger bg-paper px-4 py-2 font-mono font-bold text-danger shadow-[4px_4px_0_0_rgb(var(--color-danger))] transition-all hover:bg-danger hover:text-ink hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none active:translate-x-[4px] active:translate-y-[4px] active:shadow-none"
                            form={`delete-note-${note.id}`}
                            type="submit"
                          >
                            <Cross2Icon /> 删除笔记
                          </button>
                          <SubmitButton className="btn-brutal" pendingText="SAVING...">更新笔记</SubmitButton>
                        </div>
                      </ActionForm>
                      <ActionForm
                        id={`delete-note-${note.id}`}
                        action={deleteNoteAction}
                        onSuccess={close}
                        className="hidden"
                        confirmMessage="Delete this journal entry?"
                      >
                        <input type="hidden" name="id" value={note.id} />
                      </ActionForm>
                    </div>
                  )}
                </BrutalDialog>
                
                <p className="font-serif text-lg leading-relaxed whitespace-pre-wrap mt-2 relative group-hover/note:z-10">{note.body}</p>
              </div>
            )}
          </article>
        ))}
      </div>
      </div>
    </details>
  );
}
